import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import {
	SpaceRenderState,
	TileGridRenderState,
	TileGroupRenderState,
	TileRenderState
} from "../common";
import { GridArea, GridPosition } from "client/Api/Workspace/common";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { TileGroupAccessKind } from "client/Api/Workspace/workspace";
import { GridItem, GridState } from "client_legacy/Workspace/gridState";
import { IPackingAlgorithm } from "client_legacy/Workspace/Packing/common";
import * as tileGridSelectors from "./tileGrid";
import { TopLeftPackingAlgorithm } from "client_legacy/Workspace/Packing/topLeft";

export function generateStateOfTileGroupsConsumingSpace(
	renderState: Pick<SpaceRenderState, "groups" | "tileGroupGridDimensionLimits">
): GridState<TileGroupRenderState> {
	let groupRenderStates: readonly TileGroupRenderState[] = renderState.groups;

	if (groupRenderStates.length === 0) {
		return new GridState<TileGroupRenderState>([]);
	}

	// The groups in the array are in the DOM render order, for visual placement we need to sort them
	// by the order
	groupRenderStates = sortBy(
		groupRenderStates,
		(x) => x.order
	);

	const gridItems: GridItem<TileGroupRenderState>[] = [];
	let currentColumn: number = 0;

	for (const tileGroupRenderState of groupRenderStates) {
		if (!tileGroupRenderState.isVisible) {
			continue;
		}

		const gridItem: GridItem<TileGroupRenderState> = {
			position: {
				column: currentColumn++,
				row: 0,
			},
			dimension: tileGroupRenderState.dimension,
			item: tileGroupRenderState
		};

		gridItems.push(gridItem);
	}

	return new GridState<TileGroupRenderState>(gridItems)
		.apply(
			getTileGroupGridPackingAlgorithm(renderState)
		);
}

export function selectTileDropTarget(
	currentState: SpaceRenderState,
	tileBeingDragged: TileRenderState,
	unboundGlobalPosition: GridPosition
): TileDropTarget {
	const { regularGroupWidthTileGridConfiguration } = currentState;

	const amountOfCellsInARegularWidthTileGroup: number = regularGroupWidthTileGridConfiguration.dimensionLimits
		.columns;

	let smallestDifference = Number.MAX_VALUE;
	let tileGroupId: DatabaseOrTempObjectId | null = null;
	let placementArea: GridArea | null = null;

	const visuallySortedGroups: TileGroupRenderState[] = sortBy(
		currentState.groups,
		(x) => x.order
	);

	for (const groupRenderState of visuallySortedGroups) {
		if (groupRenderState.data.accessKind !== TileGroupAccessKind.Edit
			|| !groupRenderState.isVisible
		) {
			continue;
		}

		if (groupRenderState.tileGrid.dimension.columnSpan
			< tileBeingDragged.data.dimension.columnSpan
		) {
			continue;
		}

		const globalTileGridPositionForGroup: GridPosition = {
			// The tile grid starts 1 row from the top of the tile group
			row: groupRenderState.position.row + 1,
			// there is a 1 cell gutter between tile groups
			column: (amountOfCellsInARegularWidthTileGroup + 1) * groupRenderState.position.column
		};

		// the original relative position can be well outside of grid bounds, it's ok
		const relativeTileGridPosition: GridPosition = {
			column: unboundGlobalPosition.column - globalTileGridPositionForGroup.column,
			row: unboundGlobalPosition.row - globalTileGridPositionForGroup.row
		};

		const placeholderArea: GridArea = {
			position: relativeTileGridPosition,
			dimension: tileBeingDragged.dimension
				.dimension
		};

		// fit it to grid bounds
		const placeholderAreaFitToBounds: GridArea = fitTileAreaToTileGridBounds(
			placeholderArea,
			groupRenderState.tileGrid
		);

		// calculate how close is the actual position to the valid drop position inside the tile grid
		// NOTE: not entirely smooth as it's calculated based on column/row coordinates and not pixels
		const rowDifference = placeholderAreaFitToBounds.position.row - placeholderArea.position.row;
		const columnDifference = placeholderAreaFitToBounds.position.column - placeholderArea.position.column;
		const difference = rowDifference * rowDifference + columnDifference * columnDifference;

		// update the target
		// note that we resolve ties favouring the tile grids at the tail of the list, as it minimizes tile group reorderings
		if (difference <= smallestDifference) {
			smallestDifference = difference;
			tileGroupId = groupRenderState.data
				.id;

			placementArea = placeholderAreaFitToBounds;
		}
	}

	if (placementArea === null || tileGroupId === null) {
		throw new Error("Cannot find potential drop location");
	}

	return {
		placementArea,
		tileGroupId
	};
}

export type TileDropTarget = {
	tileGroupId: DatabaseOrTempObjectId;
	placementArea: GridArea;
};

function fitTileAreaToTileGridBounds(currentState: GridArea, gridRenderState: TileGridRenderState): GridArea {
	const state: GridState<TileRenderState> = tileGridSelectors
		.generateStateOfTilesConsumingSpace(gridRenderState);

	const maxColumns: number = gridRenderState.settings
		.configuration
		.dimensionLimits
		.columns;

	// maximum row number where the tile top left corner can be placed (including growth limits)
	// note that a grid is allowed to grow indefinitely to the bottom as long as the top left corner is in the growth limits
	const rowGrowthLimit = 1;
	const maxRowStart = state.dimension.rowSpan - 1 + rowGrowthLimit;

	let column: number = currentState.position.column;
	let row: number = currentState.position.row;

	// fit column to bounds
	if (currentState.dimension.columnSpan > maxColumns) {
		throw new Error(`Area with column span ${currentState.dimension.columnSpan} cannot fit into area of column span ${maxColumns}`);
	}

	if (column + currentState.dimension.columnSpan > maxColumns) {
		column = maxColumns - currentState.dimension.columnSpan;
	}
	if (column < 0) {
		column = 0;
	}

	// fit row to bounds
	if (row > maxRowStart) {
		row = maxRowStart;
	}
	else if (row < 0) {
		row = 0;
	}

	return {
		position: { column, row },
		dimension: { ...currentState.dimension }
	};
}

export function getTileGroupGridPackingAlgorithm(renderState: Pick<SpaceRenderState, "tileGroupGridDimensionLimits">): IPackingAlgorithm {
	return new TopLeftPackingAlgorithm(
		renderState.tileGroupGridDimensionLimits
			.maxColumns
	);
}